import React, { useEffect, useRef, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "@store/store.tsx";
import { logError, logInfo } from "@tools/LogsHandler.tsx";
import {
    fetchUserDataAsync,
    loginWithCodeAsync, resetAuthTokenActionCode,
    resetAuthTokenError,
    resetAuthTokenState,
    selectProfileAsync
} from "@store/slices/authSlice.tsx";
import { Button, Form, Input, message, Flex, Grid, Select } from "antd";
import { FacebookFilled, LoadingOutlined, WhatsAppOutlined, MailOutlined } from "@ant-design/icons";
import { CountryCode, E164Number } from "libphonenumber-js";
import PhoneInput from '@tools/components/PhoneInput';
import { getFormUrl } from '@tools/conns/PathHandler.tsx';
import { InputMode } from '@login/types/login.types.ts';
import { CodeInput } from '@tools/components/commonFields.tsx';

import '@login/styles/login.scss';

const PASS_CHANGE_SUPPORT_COPY =
    'También reenviamos el enlace de invitación de staff si el anterior venció.';

const _DEFAULT_COOL_DOWN = 15;
const DEFAULT_BUTTON_SHAPE = 'default';
const DEFAULT_BUTTON_RADIUS = '8px';

interface ErrorMessageProps {
    error?: string | string[] | null; // Puede ser una cadena, un array o null
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ error }) => {
    if (!error) return null;

    return (
        <div className="login-error-msg" role="alert" aria-live="assertive">
            {Array.isArray(error)
                ? error.map((err, index) => <p key={`${err}-${index}`}>⚠️ {err}</p>)
                : <p>⚠️ {error}</p>}
        </div>
    );
};

interface FacebookLoginButtonProps {
    onClose?: () => void;
}

export const FacebookLoginButton: React.FC<FacebookLoginButtonProps> = ({ onClose }) => {
    const dispatch = useAppDispatch();
    const windowObjectReference = useRef<Window | null>(null);
    const previousUrl = useRef<string | null>(null);
    const popupCheckInterval = useRef<number | null>(null);
    const crossOriginDetected = useRef<boolean>(false);
    const startTime = useRef<number | null>(null);
    const isPopupActive = useRef<boolean>(false);
    const [loginStatus, setLoginStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
    const radius = DEFAULT_BUTTON_RADIUS;

    // Tiempo máximo de espera en milisegundos (e.g., 5 minutos)
    const MAX_WAIT_TIME = 5 * 60 * 1000;

    useEffect(() => {
        const loginChannel = new BroadcastChannel('loginChannel');

        loginChannel.onmessage = (event) => {
            logInfo('Mensaje recibido:', event.data);
            setLoginStatus('success');
            dispatch(fetchUserDataAsync({ forceUpdate: true }));
            logInfo('---> FacebookLoginButton - fetchUserDataAsync');
            loginChannel.close();    // Cierra el canal después del primer mensaje
            // Cerrar el modal al recibir el mensaje exitoso
            onClose?.();
        };

        return () => {
            loginChannel.close();  // Asegúrate de cerrar el canal al desmontar
        };
    }, [onClose]);  // eslint-disable-line react-hooks/exhaustive-deps

    const cleanup = (wasSuccessful: boolean = false) => {
        if (popupCheckInterval.current) {
            clearInterval(popupCheckInterval.current);
            popupCheckInterval.current = null;
        }
        if (windowObjectReference.current) {
            try {
                // Solo cerramos el popup si el proceso fue exitoso o si el tiempo límite expiró
                if (
                    wasSuccessful ||
                    (startTime.current && Date.now() - startTime.current > MAX_WAIT_TIME)
                ) {
                    windowObjectReference.current.close();
                }
            } catch (e) {
                // Ignorar errores al intentar cerrar la ventana cross-origin
                logError('FacebookLoginButton - cleanup - error: ', e);
            }
            windowObjectReference.current = null;
        }
        isPopupActive.current = false;
        startTime.current = null;
        crossOriginDetected.current = false;

        if (!wasSuccessful) {
            setLoginStatus('idle');
        }
    };

    const PopupCenter = (url: string, title: string, w: number, h: number) => {
        const dualScreenLeft = window.screenX !== undefined ? window.screenX : 0;
        const dualScreenTop = window.screenY !== undefined ? window.screenY : 0;
        const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
        const height = window.innerHeight || document.documentElement.clientHeight || screen.height;
        const left = (width / 2 - w / 2) + dualScreenLeft;
        const top = (height / 2 - h / 2) + dualScreenTop;

        const popupFeatures = `
            scrollbars=yes,
            width=${w},
            height=${h},
            top=${top},
            left=${left}
        `.replace(/\s+/g, '');

        try {
            // Limpiar cualquier sesión anterior
            cleanup();

            windowObjectReference.current = window.open(url, title, popupFeatures);
            if (windowObjectReference.current) {
                logInfo('Popup iniciado');
                startTime.current = Date.now();
                setLoginStatus('pending');
            } else {
                logError('El navegador bloqueó el popup');
                setLoginStatus('error');
            }
            previousUrl.current = url;
        } catch (error) {
            logError('Error al abrir el popup:', error);
            setLoginStatus('error');
        }
    };

    useEffect(() => {
        return cleanup;
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const openLoginPopup = () => {
        const AUTH_GATE_FB = `${getFormUrl('auth:login')}?method=fb`;
        PopupCenter(AUTH_GATE_FB, 'LoginPopUp', 450, 600);
    };

    return (
        <Button
            type="primary"
            block
            icon={<FacebookFilled />}
            onClick={openLoginPopup}
            disabled={loginStatus === 'pending'}
            className="facebook-btn"
            shape={DEFAULT_BUTTON_SHAPE}
            style={{ height: '48px', borderRadius: radius, fontWeight: 500 }}
        >
            {loginStatus === 'pending' ? (
                <>
                    <LoadingOutlined spin />
                    {' Conectando con Facebook...'}
                </>
            ) : (
                'Iniciar sesión con Facebook'
            )}
        </Button>
    );
};

// Interface for the component props
interface WhatsAppLoginInputProps {
    formMode: InputMode;
}

// Componente WhatsAppLoginInput
export const WhatsAppLoginInput: React.FC<WhatsAppLoginInputProps> = ({ formMode = 'login' }) => {
    const [phoneNumber, setPhoneNumber] = useState<E164Number>();
    const [countryCode, setCountryCode] = useState<CountryCode | undefined>('AR');
    const [resetCode, setResetCode] = useState(false);
    const [mode, setMode] = useState<InputMode>(formMode);
    const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>(undefined);

    const [name, setName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');

    const dispatch = useAppDispatch();
    const authToken = useAppSelector((state: RootState) => state.auth.authToken);
    const authStatus = useAppSelector((state: RootState) => state.auth.status);
    const isCodeSent = Boolean(authToken?.authToken);
    const selectionRequired = Boolean(authToken?.selectionRequired && authToken?.profiles?.length);
    const [cooldown, setCooldown] = useState(0);
    const radius = DEFAULT_BUTTON_RADIUS;
    const { md } = Grid.useBreakpoint();
    const isMobile = !md;

    useEffect(() => {
        if (authStatus === 'waiting_code') setCooldown(_DEFAULT_COOL_DOWN);
    }, [authStatus]);

    useEffect(() => {
        logInfo('>>> useEffect - authToken?.actionCode: ', authToken?.actionCode);
        if (authToken?.actionCode === 'login_wsp') {
            setMode('login');
            dispatch(resetAuthTokenActionCode());
        } else if (authToken?.actionCode === 'register_wsp') {
            setMode('register');
            dispatch(resetAuthTokenActionCode());
        } else if (authToken?.actionCode === 'pass_change_wsp') {
            setMode('pass_change');
            dispatch(resetAuthTokenActionCode());
        }
    }, [authToken?.actionCode, dispatch]);

    useEffect(() => {
        if (!selectionRequired) {
            setSelectedProfileId(undefined);
        }
    }, [selectionRequired]);

    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    const handleSendCode = async () => {
        if (!phoneNumber) {
            message.error('Por favor, ingresa un número válido.');
            return;
        }

        setSelectedProfileId(undefined);
        await dispatch(
            loginWithCodeAsync({
                tel: phoneNumber,
                countryCode,
                code: '',
                method: 'wsp',
                mode,
                ...(mode === 'register' && { name, password }),
                ...(mode === 'pass_change' && { password }),
            })
        );
        setCooldown(_DEFAULT_COOL_DOWN);
    };

    const handleResend = () => {
        void handleSendCode();
    };

    const handleLoginOrRegister = async (code: string) => {
        if (!phoneNumber || !code || !authToken) {
            message.error('Completa todos los campos antes de validar.');
            return;
        }

        let requiresProfileSelection = false;

        try {
            if (mode === 'login') {
                const res = await dispatch(
                    loginWithCodeAsync({
                        authToken: authToken.authToken,
                        tel: phoneNumber,
                        countryCode,
                        code,
                        mode,
                        method: 'wsp'
                    })
                ).unwrap();
                requiresProfileSelection = Boolean(res.selectionRequired);
                if (res.user) {
                    dispatch(fetchUserDataAsync({ forceUpdate: true }));
                }
            } else if (mode === 'register') {
                if (!name || !password || !confirmPassword) {
                    message.error('Por favor completa todos los campos.');
                    return;
                }
                if (password !== confirmPassword) {
                    message.error('Las contraseñas no coinciden.');
                    return;
                }
                const res = await dispatch(
                    loginWithCodeAsync({
                        authToken: authToken.authToken,
                        tel: phoneNumber,
                        countryCode,
                        code,
                        mode,
                        method: 'wsp',
                        name,
                        password
                    })
                ).unwrap();
                requiresProfileSelection = Boolean(res.selectionRequired);
                if (res.user) {
                    dispatch(fetchUserDataAsync({ forceUpdate: true }));
                }
            } else if (mode === 'pass_change') {
                if (!password || !confirmPassword) {
                    message.error('Por favor completa todos los campos.');
                    return;
                }
                if (password !== confirmPassword) {
                    message.error('Las contraseñas no coinciden.');
                    return;
                }
                const res = await dispatch(
                    loginWithCodeAsync({
                        authToken: authToken.authToken,
                        tel: phoneNumber,
                        countryCode,
                        code,
                        mode,
                        method: 'wsp',
                        password
                    })
                ).unwrap();
                requiresProfileSelection = Boolean(res.selectionRequired);
                if (res.user) {
                    dispatch(fetchUserDataAsync({ forceUpdate: true }));
                }
            }
        } catch {
            setCooldown(_DEFAULT_COOL_DOWN);
            return;
        }

        if (!requiresProfileSelection) {
            setTimeout(() => {
                setResetCode(true);
            }, 2000);
        }
    };

    const handleSelectProfile = async () => {
        if (!authToken?.authToken || !selectedProfileId) {
            message.error('Seleccioná un perfil para continuar.');
            return;
        }

        try {
            const res = await dispatch(
                selectProfileAsync({
                    authToken: authToken.authToken,
                    target_user_id: selectedProfileId,
                })
            ).unwrap();
            if (res.user) {
                dispatch(fetchUserDataAsync({ forceUpdate: true }));
            }
        } catch {
            // El error ya queda reflejado en el slice
        }
    };

    const profileOptions = (authToken?.profiles ?? []).map((profile) => ({
        value: profile.id,
        label: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || `Perfil ${profile.id}`,
    }));

    return (
        <div aria-busy={authStatus === 'loading'}>
            {(mode === 'register' || mode === 'pass_change') && (
                <Form layout="vertical">
                    {mode === 'register' && (
                        <Form.Item label="Nombre" required>
                            <Input placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
                        </Form.Item>
                    )}
                    <Form.Item label="Contraseña" required>
                        <Input.Password
                            placeholder="Contraseña"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Confirmar Contraseña"
                        required
                        hasFeedback
                        validateStatus={
                            password && confirmPassword && password !== confirmPassword ? 'error' : undefined
                        }
                        help={
                            password && confirmPassword && password !== confirmPassword
                                ? 'Las contraseñas no coinciden'
                                : undefined
                        }
                    >
                        <Input.Password
                            placeholder="Confirmar Contraseña"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </Form.Item>
                </Form>
            )}

            <p>
                {mode === 'pass_change'
                    ? 'Ingresá tu número de celular para recibir un código y restablecer tu acceso.'
                    : 'Ingresa tu número de celular para recibir un código de validación.'}
            </p>
            {mode === 'pass_change' && (
                <p style={{ marginTop: -8, color: 'var(--ant-color-text-tertiary, #667085)', fontSize: 13 }}>
                    {PASS_CHANGE_SUPPORT_COPY}
                </p>
            )}
            <Flex gap="small" align="start" style={{ width: '100%' }} vertical={isMobile}>
                <PhoneInput
                    id="idPhoneInput"
                    name="phoneInputWithCountrySelect"
                    placeholder="Celular"
                    onChange={setPhoneNumber}
                    onCountryChange={setCountryCode}
                    value={phoneNumber}
                    defaultCountry="AR"
                    rules={{ required: true }}
                    style={{ flex: 1, width: isMobile ? '100%' : undefined }}
                    disabled={isCodeSent || authStatus === 'loading'}
                />
                {!isCodeSent && (
                    <Button
                        onClick={handleSendCode}
                        disabled={
                            !phoneNumber ||
                            authStatus === 'loading' ||
                            (mode === 'register' &&
                                (!name || !password || !confirmPassword || password !== confirmPassword)) ||
                            (mode === 'pass_change' &&
                                (!password || !confirmPassword || password !== confirmPassword))
                        }
                        size="small"
                        icon={<WhatsAppOutlined />}
                        className="wsp-code-btn"
                        shape={DEFAULT_BUTTON_SHAPE}
                        style={{
                            borderRadius: radius,
                            fontWeight: 500,
                            ...(isMobile ? { alignSelf: 'flex-end' } : {})
                        }}
                        loading={authStatus === 'loading'}
                    >
                        {authToken?.error ? 'Enviar otro código' : 'Enviar código'}
                    </Button>
                )}
            </Flex>
            {!isCodeSent && <ErrorMessage error={authToken?.error} />}

            {isCodeSent && !selectionRequired && (
                <>
                    <p>
                        {mode === 'pass_change'
                            ? 'Ingresá el código recibido para actualizar tu contraseña o activar tu invitación.'
                            : 'Ingresa el código de validación recibido.'}
                    </p>
                    <CodeInput
                        digits={6}
                        onComplete={handleLoginOrRegister}
                        digitBox={{ style: { marginRight: 5, width: 45 } }}
                        resetCode={resetCode}
                        setResetCode={setResetCode}
                        onChange={() => {
                            dispatch(resetAuthTokenError());
                        }}
                        disabled={authStatus === 'loading'}
                    />
                    <ErrorMessage error={authToken?.error} />
                    <Button
                        type="link"
                        size="small"
                        onClick={handleResend}
                        disabled={cooldown > 0 || authStatus === 'loading'}
                    >
                        {cooldown > 0 ? `Reenviar código (${cooldown})` : 'Reenviar código'}
                    </Button>
                    <div className="w-100 text-right text-end">
                        <Button
                            type="text"
                            onClick={() => {
                                dispatch(resetAuthTokenState());
                                setCooldown(0);
                                setSelectedProfileId(undefined);
                            }}
                            className="text-primary bg-transparent border-0 hover_text_color_oscuro_3"
                        >
                            Editar número
                        </Button>
                    </div>
                </>
            )}

            {isCodeSent && selectionRequired && (
                <>
                    <p>Encontramos más de un perfil con este celular. Elegí cuál querés usar para continuar.</p>
                    <Form layout="vertical">
                        <Form.Item label="Perfil" required>
                            <Select
                                placeholder="Seleccioná un perfil"
                                options={profileOptions}
                                value={selectedProfileId}
                                onChange={(value) => setSelectedProfileId(value)}
                                disabled={authStatus === 'loading'}
                            />
                        </Form.Item>
                    </Form>
                    <ErrorMessage error={authToken?.error} />
                    <Flex align="center" justify="space-between" wrap="wrap" gap="small">
                        <Button
                            type="link"
                            size="small"
                            onClick={handleResend}
                            disabled={cooldown > 0 || authStatus === 'loading'}
                        >
                            {cooldown > 0 ? `Reenviar código (${cooldown})` : 'Reenviar código'}
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSelectProfile}
                            disabled={!selectedProfileId || authStatus === 'loading'}
                            loading={authStatus === 'loading'}
                        >
                            Continuar con este perfil
                        </Button>
                    </Flex>
                    <div className="w-100 text-right text-end">
                        <Button
                            type="text"
                            onClick={() => {
                                dispatch(resetAuthTokenState());
                                setCooldown(0);
                                setSelectedProfileId(undefined);
                            }}
                            className="text-primary bg-transparent border-0 hover_text_color_oscuro_3"
                        >
                            Editar número
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

// Interfaz para las propiedades del componente
interface EmailLoginInputProps {
    formMode: InputMode;
}

// Componente EmailLoginInput
export const EmailLoginInput: React.FC<EmailLoginInputProps> = ({ formMode = 'register' }) => {
    // Estados para almacenar email, nombre y contraseña
    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [resetCode, setResetCode] = useState(false);
    const [mode, setMode] = useState<InputMode>(formMode);

    // Acceso al dispatch de Redux y al estado de la aplicación
    const dispatch = useAppDispatch();
    const authToken = useAppSelector((state: RootState) => state.auth.authToken);
    const authStatus = useAppSelector((state: RootState) => state.auth.status);
    const isCodeSent = Boolean(authToken?.authToken);
    const [cooldown, setCooldown] = useState(0);

    const radius = DEFAULT_BUTTON_RADIUS;
    const { md } = Grid.useBreakpoint();
    const isMobile = !md;

    useEffect(() => {
        if (authStatus === 'waiting_code') setCooldown(_DEFAULT_COOL_DOWN);
    }, [authStatus]);

    // useEffect para manejar cambios en el authToken y actualizar el modo
    useEffect(() => {
        logInfo('>>> useEffect - authToken?.actionCode: ', authToken?.actionCode);
        if (authToken?.actionCode === 'login_email') {
            setMode('login');
            dispatch(resetAuthTokenActionCode());
        } else if (authToken?.actionCode === 'register_email') {
            setMode('register');
            dispatch(resetAuthTokenActionCode());
        } else if (authToken?.actionCode === 'pass_change_email') {
            setMode('pass_change');
            dispatch(resetAuthTokenActionCode());
        }
    }, [authToken?.actionCode, dispatch]);

    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    const handleSendCode = async () => {
        if (!email) {
            message.error('Por favor, ingresa un email válido.');
            return;
        }

        await dispatch(
            loginWithCodeAsync({
                email,
                code: '',
                mode,
                method: 'email',
                ...(mode === 'register' && { name, password }),
                ...(mode === 'pass_change' && { password }),
            })
        );
        setCooldown(_DEFAULT_COOL_DOWN);
    };

    const handleResend = () => {
        void handleSendCode();
    };

    // Función para manejar el login, registro o cambio de contraseña después de ingresar el código
    const handleLoginOrRegister = async (code: string) => {
        if (!email || !code || !authToken) {
            message.error('Completa todos los campos antes de validar.');
            return;
        }

        try {
            if (mode === 'login') {
                // Dispatch para login
                const res = await dispatch(
                    loginWithCodeAsync({
                        authToken: authToken.authToken,
                        email,
                        code,
                        mode,
                        method: 'email'
                    })
                ).unwrap();
                if (res.user) {
                    dispatch(fetchUserDataAsync({ forceUpdate: true }));
                }
            } else if (mode === 'register') {
                // Validar campos de registro
                if (!name || !password || !confirmPassword) {
                    message.error('Por favor completa todos los campos.');
                    return;
                }
                if (password !== confirmPassword) {
                    message.error('Las contraseñas no coinciden.');
                    return;
                }
                // Dispatch para registro
                const res = await dispatch(
                    loginWithCodeAsync({
                        authToken: authToken.authToken,
                        email,
                        code,
                        name,
                        password,
                        mode,
                        method: 'email'
                    })
                ).unwrap();
                if (res.user) {
                    dispatch(fetchUserDataAsync({ forceUpdate: true }));
                }
            } else if (mode === 'pass_change') {
                // Validar campos de cambio de contraseña
                if (!password || !confirmPassword) {
                    message.error('Por favor completa todos los campos.');
                    return;
                }
                if (password !== confirmPassword) {
                    message.error('Las contraseñas no coinciden.');
                    return;
                }
                // Dispatch para cambio de contraseña
                const res = await dispatch(
                    loginWithCodeAsync({
                        authToken: authToken.authToken,
                        email,
                        code,
                        password,
                        mode,
                        method: 'email'
                    })
                ).unwrap();
                if (res.user) {
                    dispatch(fetchUserDataAsync({ forceUpdate: true }));
                }
            }
        } catch {
            // Al ingresar un código incorrecto se envía otro automáticamente, reinicia el enfriamiento
            setCooldown(_DEFAULT_COOL_DOWN);
        }

        // Añadir un retraso antes de reiniciar el código
        setTimeout(() => {
            setResetCode(true);
        }, 2000);
    };

    return (
        <div aria-busy={authStatus === 'loading'}>
            <Form layout="vertical" aria-busy={authStatus === 'loading'}>
                {/* Renderizar campos adicionales para registro o cambio de contraseña */}
                {(mode === 'register' || mode === 'pass_change') && (
                    <>
                        {mode === 'register' && (
                            <Form.Item label="Nombre" required>
                                <Input placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
                            </Form.Item>
                        )}
                        <Form.Item label="Contraseña" required>
                            <Input.Password
                                placeholder="Contraseña"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Confirmar Contraseña"
                            required
                            hasFeedback
                            validateStatus={
                                password && confirmPassword && password !== confirmPassword ? 'error' : undefined
                            }
                            help={
                                password && confirmPassword && password !== confirmPassword
                                    ? 'Las contraseñas no coinciden'
                                    : undefined
                            }
                        >
                            <Input.Password
                                placeholder="Confirmar Contraseña"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </Form.Item>
                    </>
                )}

                {/* Entrada del email */}
                <p>
                    {mode === 'pass_change'
                        ? 'Ingresá tu email para recibir un código y restablecer tu acceso.'
                        : 'Ingresa tu email para recibir un código de validación.'}
                </p>
                {mode === 'pass_change' && (
                    <p style={{ marginTop: -8, color: 'var(--ant-color-text-tertiary, #667085)', fontSize: 13 }}>
                        {PASS_CHANGE_SUPPORT_COPY}
                    </p>
                )}
                <Flex gap="small" align="start" style={{ width: '100%' }} vertical={isMobile}>
                    <Input
                        id="idEmailInput"
                        name="emailInput"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ flex: 1, width: isMobile ? '100%' : undefined }}
                        disabled={isCodeSent || authStatus === 'loading'}
                    />
                    {!isCodeSent && (
                        <Button
                            onClick={handleSendCode}
                            disabled={
                                !email ||
                                authStatus === 'loading' ||
                                (mode === 'register' &&
                                    (!name || !password || !confirmPassword || password !== confirmPassword)) ||
                                (mode === 'pass_change' &&
                                    (!password || !confirmPassword || password !== confirmPassword))
                            }
                            size="small"
                            icon={<MailOutlined />}
                            className="email-code-btn"
                            shape={DEFAULT_BUTTON_SHAPE}
                            style={{
                                borderRadius: radius,
                                fontWeight: 500,
                                ...(isMobile ? { alignSelf: 'flex-end' } : {})
                            }}
                            loading={authStatus === 'loading'}
                        >
                            {authToken?.error ? 'Enviar otro código' : 'Enviar código'}
                        </Button>
                    )}
                </Flex>
                {!isCodeSent && <ErrorMessage error={authToken?.error} />}
            </Form>

            {isCodeSent && (
                <>
                    <p>
                        {mode === 'pass_change'
                            ? 'Ingresá el código recibido para actualizar tu contraseña o activar tu invitación.'
                            : 'Ingresa el código de validación recibido.'}
                    </p>
                    <CodeInput
                        digits={6}
                        onComplete={handleLoginOrRegister}
                        digitBox={{ style: { marginRight: 5, width: 45 } }}
                        resetCode={resetCode}
                        setResetCode={setResetCode}
                        onChange={() => {
                            dispatch(resetAuthTokenError());
                        }}
                        disabled={authStatus === 'loading'}
                    />
                    <ErrorMessage error={authToken?.error} />
                    <Button
                        type="link"
                        size="small"
                        onClick={handleResend}
                        disabled={cooldown > 0 || authStatus === 'loading'}
                    >
                        {cooldown > 0 ? `Reenviar código (${cooldown})` : 'Reenviar código'}
                    </Button>
                    <div className="w-100 text-right text-end">
                        <Button
                            type="text"
                            onClick={() => {
                                dispatch(resetAuthTokenState());
                                setCooldown(0);
                            }}
                            className="text-primary bg-transparent border-0 hover_text_color_oscuro_3"
                        >
                            Editar email
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
